<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable prettier/prettier, vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, inject, proxyRefs, ref } from 'vue';
import { useImudeiRuntimeModel } from './useImudeiRuntimeModel';

const emit = defineEmits(['navigate']);
const realData = inject('imudeiRealData');
const { error, saving } = realData;
const model = proxyRefs(
  useImudeiRuntimeModel({
    realData,
    module: computed(() => 'visits'),
    screen: computed(() => 'detail'),
  })
);

const activeAction = ref('');

const asArray = value => (Array.isArray(value) ? value : []);
const pathValue = (value, path) =>
  path.split('.').reduce((current, key) => current?.[key], value);
const firstValue = (value, paths, fallback = '') => {
  const result = paths
    .map(path => pathValue(value, path))
    .find(item => item !== undefined && item !== null && item !== '');

  return result ?? fallback;
};
const valuesAt = (value, paths) =>
  paths.flatMap(path => asArray(pathValue(value, path)));
const initials = value =>
  String(value || '—')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(item => item[0])
    .join('')
    .toLocaleUpperCase('pt-BR');
const formatDateTime = value => {
  if (!value) return 'Não retornado pela API';

  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return String(value);

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};
const formatDate = value => {
  if (!value) return 'Não retornada pela API';

  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return String(value);

  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'long' }).format(date);
};
const formatTime = value => {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return String(value);

  return new Intl.DateTimeFormat('pt-BR', { timeStyle: 'short' }).format(date);
};
const imageUrl = image => {
  if (typeof image === 'string') return image;
  if (!image || typeof image !== 'object') return '';

  return firstValue(image, ['url', 'file_url', 'image_url', 'cover_url']);
};
const textValue = value => (value == null ? '' : String(value));
const actionIcon = name =>
  ({
    checkIn: 'i-lucide-map-pin-check',
    confirm: 'i-lucide-circle-check',
    finalizeOutcome: 'i-lucide-clipboard-check',
    finish: 'i-lucide-flag',
    reschedule: 'i-lucide-calendar-clock',
  })[name] || 'i-lucide-ellipsis';
const statusClass = value => {
  const status = textValue(value).toLocaleLowerCase('pt-BR');
  if (/confirm|concl|finaliz|realiz/.test(status)) {
    return 'border-emerald-100 bg-emerald-50 text-emerald-700';
  }
  if (/cancel|recus|falh/.test(status)) {
    return 'border-rose-100 bg-rose-50 text-rose-700';
  }
  if (/agend|solicit|pend/.test(status)) {
    return 'border-violet-100 bg-violet-50 text-violet-700';
  }
  return 'border-[#e8e4ef] bg-[#faf9fc] text-[#667394]';
};

const source = computed(() => model.detail?.source || {});
const detailAvailable = computed(() => Boolean(model.detail));
const actionError = computed(() => model.localError || error.value || '');
const status = computed(() =>
  model.detail?.status || firstValue(source.value, ['status_label', 'status'])
);
const visitCode = computed(() =>
  firstValue(
    source.value,
    ['visit_code', 'reference', 'code'],
    model.detail?.id ? `Visita #${model.detail.id}` : 'Visita indisponível'
  )
);
const propertyTitle = computed(() =>
  model.detail?.title ||
  firstValue(
    source.value,
    ['imovel.titulo', 'imovel.title', 'property.title', 'listing.title'],
    'Imóvel não retornado pela API'
  )
);
const propertyAddress = computed(() =>
  firstValue(source.value, [
    'imovel.endereco',
    'property.address',
    'address.full_address',
    'address.endereco',
  ])
);
const propertyLocation = computed(() =>
  [
    firstValue(source.value, [
      'imovel.bairro',
      'property.neighborhood',
      'address.neighborhood',
    ]),
    firstValue(source.value, [
      'imovel.cidade',
      'property.city',
      'address.city',
    ]),
    firstValue(source.value, ['imovel.uf', 'property.state', 'address.state']),
  ]
    .filter(Boolean)
    .join(' · ')
);
const propertyImage = computed(() => {
  const directImage = firstValue(source.value, [
    'imovel.cover_url',
    'imovel.image_url',
    'property.cover_url',
    'property.image_url',
    'listing.cover_url',
    'listing.image_url',
    'image_url',
  ]);
  const galleryImage = [
    source.value.imovel?.images?.[0],
    source.value.imovel?.photos?.[0],
    source.value.property?.images?.[0],
    source.value.property?.photos?.[0],
    source.value.imovel?.midias?.[0],
    source.value.imovel?.media?.[0],
    source.value.property?.midias?.[0],
    source.value.property?.media?.[0],
    source.value.listing?.images?.[0],
  ]
    .map(imageUrl)
    .find(Boolean);

  return directImage || galleryImage || '';
});
const propertyFacts = computed(() => {
  const facts = [
    [
      'i-lucide-bed-double',
      firstValue(source.value, [
        'imovel.quartos',
        'imovel.bedrooms',
        'property.quartos',
        'property.bedrooms',
        'listing.bedrooms',
      ]),
      'dorms.',
    ],
    [
      'i-lucide-bath',
      firstValue(source.value, [
        'imovel.banheiros',
        'imovel.bathrooms',
        'property.banheiros',
        'property.bathrooms',
        'listing.bathrooms',
      ]),
      'banheiros',
    ],
    [
      'i-lucide-car-front',
      firstValue(source.value, [
        'imovel.vagas_garagem',
        'imovel.parking_spaces',
        'property.vagas_garagem',
        'property.parking_spaces',
        'listing.parking_spaces',
      ]),
      'vagas',
    ],
    [
      'i-lucide-ruler',
      firstValue(source.value, [
        'imovel.area_util',
        'property.area_util',
        'property.useful_area',
        'listing.area_util',
      ]),
      'm²',
    ],
  ]
    .filter(([, value]) => value !== '')
    .map(([icon, value, label]) => ({ icon, label: `${value} ${label}` }));

  return facts.length
    ? facts
    : model.detailFacts.slice(0, 4).map(item => ({
        icon: 'i-lucide-house',
        label: `${item.label}: ${item.value}`,
      }));
});

const participantFrom = (candidate, role) => {
  if (!candidate) return null;

  const name = firstValue(candidate, [
    'name',
    'full_name',
    'contact.name',
    'user.name',
    'display_name',
  ]);
  if (!name) return null;

  return {
    avatar: firstValue(candidate, [
      'avatar_url',
      'thumbnail',
      'contact.thumbnail',
      'user.avatar_url',
    ]),
    email: firstValue(candidate, ['email', 'contact.email', 'user.email']),
    id: String(candidate.id || candidate.user_id || name),
    initials: initials(name),
    name,
    phone: firstValue(candidate, [
      'phone_number',
      'phone',
      'contact.phone_number',
      'contact.phone',
    ]),
    role,
  };
};
const primaryParticipants = computed(() => {
  const client = [source.value.contact, source.value.client, source.value.visitor]
    .map(candidate => participantFrom(candidate, 'Cliente'))
    .find(Boolean);
  const broker = [
    source.value.user,
    source.value.agent,
    source.value.broker,
    source.value.assigned_to,
    source.value.assignedTo,
    source.value.responsible,
  ]
    .map(candidate => participantFrom(candidate, 'Corretor responsável'))
    .find(Boolean);

  return [
    client || { id: 'client-unavailable', role: 'Cliente', unavailable: true },
    broker || {
      id: 'broker-unavailable',
      role: 'Corretor responsável',
      unavailable: true,
    },
  ];
});
const additionalParticipants = computed(() =>
  valuesAt(source.value, [
    'participants',
    'additional_participants',
    'guests',
  ])
    .map(candidate => participantFrom(candidate, 'Participante'))
    .filter(Boolean)
    .filter(
      (participant, index, list) =>
        list.findIndex(item => item.id === participant.id) === index
    )
    .slice(0, 4)
);

const scheduledAt = computed(() =>
  firstValue(source.value, ['scheduled_at', 'scheduled_for', 'starts_at'])
);
const endsAt = computed(() =>
  firstValue(source.value, ['ends_at', 'scheduled_end_at', 'end_at'])
);
const visitType = computed(() =>
  firstValue(source.value, ['visit_type', 'type'], 'Não retornado pela API')
);
const createdAt = computed(() => firstValue(source.value, ['created_at']));
const scheduledBy = computed(() =>
  firstValue(source.value, [
    'created_by.name',
    'scheduled_by.name',
    'user.name',
    'agent.name',
  ])
);

const accessInstructions = computed(() =>
  firstValue(source.value, [
    'access_instructions',
    'access_notes',
    'additional_attributes.entry_access.instructions',
    'additional_attributes.entry_access.helper',
    'property.access_instructions',
    'imovel.access_instructions',
  ])
);
const accessDetails = computed(() =>
  [
    {
      label: 'Acesso',
      value: firstValue(source.value, [
        'additional_attributes.entry_access.access_label',
      ]),
    },
    {
      label: 'Chave',
      value: firstValue(source.value, [
        'additional_attributes.entry_access.key_type_label',
      ]),
    },
    {
      label: 'Portaria',
      value: firstValue(source.value, [
        'additional_attributes.entry_access.doorman_label',
      ]),
    },
    {
      label: 'Ocupação',
      value: firstValue(source.value, [
        'additional_attributes.entry_access.house_occupancy_label',
      ]),
    },
  ].filter(item => item.value && !['-', '—'].includes(String(item.value)))
);

const activityLabel = event =>
  firstValue(event, [
    'label',
    'title',
    'body',
    'action',
    'event_type',
    'tipo',
    'notes',
    'reason',
    'status_novo',
  ]) || 'Atualização registrada';
const rawActivities = computed(() => {
  const rawEvents = valuesAt(source.value, [
    'timeline',
    'events',
    'history',
    'imovel.visit_history',
  ]);
  if (rawEvents.length) return rawEvents;

  return model.history;
});
const activityFeed = computed(() =>
  rawActivities.value.slice(0, 5).map((event, index) => ({
    actor:
      firstValue(event, [
        'actor.name',
        'actor_name',
        'agent_name',
        'user.name',
        'author.name',
      ]) || 'Sistema',
    avatar: firstValue(event, [
      'actor.avatar_url',
      'actor_avatar_url',
      'user.avatar_url',
      'author.avatar_url',
    ]),
    date:
      event.date ||
      formatDateTime(
        firstValue(event, [
          'occurred_at',
          'created_at',
          'updated_at',
          'scheduled_at',
          'at',
        ])
      ),
    id: String(event.id || event.sequence || `${activityLabel(event)}-${index}`),
    label: activityLabel(event),
  }))
);
const timelineStages = computed(() => {
  const events = activityFeed.value;
  const eventFor = expression =>
    events.find(event => expression.test(`${event.label} ${event.actor}`));
  const stageDefinitions = [
    {
      event: scheduledAt.value ? { date: formatDateTime(scheduledAt.value) } : null,
      icon: 'i-lucide-calendar-days',
      label: 'Agendada',
      present: Boolean(scheduledAt.value),
    },
    {
      event: eventFor(/confirm/i),
      icon: 'i-lucide-circle-check',
      label: 'Confirmada',
      present: /confirm/i.test(status.value || ''),
    },
    {
      event: eventFor(/check.?in|realiz|finish|concl/i),
      icon: 'i-lucide-users-round',
      label: 'Realizada',
      present: /check.?in|realiz|finish|concl/i.test(status.value || ''),
    },
    {
      event: eventFor(/result|outcome/i),
      icon: 'i-lucide-star',
      label: 'Resultado',
      present: Boolean(
        firstValue(source.value, ['visit_outcome', 'outcome', 'result'])
      ),
    },
  ];

  return stageDefinitions.map(stage => ({
    ...stage,
    date: stage.event?.date || 'Não retornado pela API',
    complete: Boolean(stage.event?.date) || stage.present,
  }));
});

const checklistItems = computed(() =>
  valuesAt(source.value, [
    'checklist',
    'visit_checklist',
    'additional_attributes.checklist',
    'additional_attributes.visit_checklist',
  ])
    .map((item, index) => {
      const value = typeof item === 'object' && item ? item : { title: item };
      const statusValue = firstValue(value, ['status']);

      return {
        complete:
          value.completed === true ||
          value.done === true ||
          value.checked === true ||
          /concl|done|check/i.test(statusValue),
        id: String(value.id || value.key || `${value.title || item}-${index}`),
        note: firstValue(value, [
          'completed_by.name',
          'user.name',
          'responsible.name',
          'updated_at',
        ]),
        title: firstValue(value, ['title', 'label', 'name', 'description']),
      };
    })
    .filter(item => item.title)
);
const attachments = computed(() =>
  valuesAt(source.value, [
    'attachments',
    'documents',
    'files',
    'imovel.attachments',
    'imovel.documents',
    'property.attachments',
    'property.documents',
  ])
    .map((file, index) => {
      const value = typeof file === 'object' && file ? file : { name: file };
      return {
        id: String(value.id || value.key || `${value.name || file}-${index}`),
        name: firstValue(value, [
          'name',
          'filename',
          'file_name',
          'original_filename',
          'title',
        ]),
        size: firstValue(value, ['size_label', 'file_size_label', 'size']),
        type: firstValue(value, ['content_type', 'type', 'extension']),
      };
    })
    .filter(file => file.name)
    .slice(0, 3)
);
const actionableOptions = computed(() => model.actionOptions.slice(0, 3));

const runAction = async action => {
  if (saving.value || !model.actionOptions.some(option => option.name === action)) {
    return;
  }

  activeAction.value = action;
  try {
    await model.runAction(action);
  } finally {
    activeAction.value = '';
  }
};
</script>

<template>
  <main
    data-imudei-visual-id="visit-detail-root"
    class="relative ml-[29px] w-[1263px] max-w-none pb-10 pt-2 text-[#101958]"
  >
    <header class="flex h-[67px] items-start justify-between gap-5">
      <div>
        <div class="flex items-center gap-2 text-[9px] text-[#7480a0]">
          <span>Visitas</span><span class="i-lucide-chevron-right size-3" /><span>{{ visitCode }}</span>
        </div>
        <div class="mt-2 flex items-center gap-3">
          <h1 class="text-[26px] font-bold tracking-[-0.035em]">Visita — {{ propertyTitle }}</h1>
          <span v-if="status" class="rounded-full border px-3 py-1 text-[9px] font-semibold" :class="statusClass(status)">{{ status }}</span>
        </div>
      </div>
      <div class="flex items-center gap-3 pt-2">
        <button type="button" class="flex h-10 items-center gap-2 rounded-lg border border-[#8757ed] bg-white px-5 text-[10px] font-semibold text-[#5721c8] disabled:cursor-not-allowed disabled:border-[#e5dff0] disabled:text-[#a29ab3]" :disabled="saving || !model.canEdit" @click="emit('navigate', 'edit')"><span class="i-lucide-pencil size-4" />{{ model.canEdit ? 'Editar visita' : 'Edição indisponível' }}</button>
        <button v-for="action in actionableOptions" :key="action.name" type="button" class="flex h-10 items-center gap-2 rounded-lg border border-[#e0d9ee] bg-white px-4 text-[10px] font-semibold text-[#4c356f] disabled:cursor-not-allowed disabled:opacity-50" :disabled="saving" @click="runAction(action.name)"><span :class="actionIcon(action.name)" class="size-4" />{{ activeAction === action.name ? 'Executando…' : action.label }}</button>
        <span v-if="!actionableOptions.length" class="flex h-10 items-center rounded-lg border border-dashed border-[#ddd7e7] px-4 text-[10px] text-[#8c94aa]">Ações indisponíveis</span>
      </div>
    </header>

    <p v-if="actionError" aria-live="polite" class="mb-3 w-[820px] rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-[10px] text-rose-700">{{ actionError }}</p>
    <p v-else-if="model.notice" aria-live="polite" class="mb-3 w-[820px] rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-[10px] text-emerald-700">{{ model.notice }}</p>

    <section class="grid grid-cols-[820px_419px] items-start gap-[24px]">
      <section class="min-w-0 space-y-[9px]">
        <article class="grid h-[220px] grid-cols-[430px_minmax(0,1fr)] overflow-hidden rounded-2xl border border-[#eeeaf0] bg-white p-3 shadow-[0_3px_15px_rgba(51,31,93,0.03)]">
          <div class="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#eee9f9] to-[#f8f6fb]">
            <img v-if="propertyImage" :src="propertyImage" :alt="propertyTitle" class="h-full w-full object-cover" />
            <div v-else class="grid h-full place-items-center text-[#8067af]"><span class="i-lucide-image-off size-9" /></div>
            <span class="absolute bottom-3 left-3 flex items-center gap-2 rounded-lg bg-[#171539]/70 px-3 py-1.5 text-[9px] font-medium text-white"><span class="i-lucide-images size-3" />{{ propertyImage ? 'Mídia retornada pela API' : 'Mídia indisponível' }}</span>
          </div>
          <div class="flex min-w-0 flex-col justify-center px-5">
            <span class="flex items-center gap-2 text-[9px] font-semibold text-[#4e22be]"><span class="i-lucide-building-2 size-4" /> Imóvel</span>
            <h2 class="mt-3 truncate text-[14px] font-bold">{{ propertyTitle }}</h2>
            <p class="mt-4 text-[10px] text-[#59678f]">{{ propertyAddress || 'Endereço não retornado pela API' }}</p>
            <p v-if="propertyLocation" class="mt-2 text-[10px] text-[#59678f]">{{ propertyLocation }}</p>
            <div v-if="propertyFacts.length" class="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-[9px] text-[#4f5d86]"><span v-for="fact in propertyFacts" :key="fact.label" class="flex items-center gap-2"><span :class="fact.icon" class="size-3.5 text-[#536891]" />{{ fact.label }}</span></div>
            <p v-else class="mt-7 text-[9px] text-[#8790a7]">Características não retornadas pela API.</p>
          </div>
        </article>

        <article class="overflow-hidden rounded-2xl border border-[#eeeaf0] bg-white shadow-[0_3px_15px_rgba(51,31,93,0.03)]">
          <div class="grid min-h-[111px] grid-cols-2 divide-x divide-[#eeeaf0]">
            <section v-for="participant in primaryParticipants" :key="participant.id" class="p-4">
              <span class="flex items-center gap-2 text-[9px] font-semibold text-[#60709a]"><span class="i-lucide-user-round size-4 text-[#5821ca]" />{{ participant.role }}</span>
              <div v-if="!participant.unavailable" class="mt-3 flex items-center gap-3">
                <img v-if="participant.avatar" :src="participant.avatar" :alt="participant.name" class="size-12 rounded-full object-cover ring-2 ring-[#7c36db] ring-offset-2" />
                <span v-else class="grid size-12 place-items-center rounded-full bg-[#f0eaff] text-[12px] font-bold text-[#5d24cb] ring-2 ring-[#7c36db] ring-offset-2">{{ participant.initials }}</span>
                <span class="min-w-0 flex-1"><strong class="block truncate text-[10px]">{{ participant.name }}</strong><small class="mt-1 block truncate text-[8px] text-[#667394]">{{ participant.phone || participant.email || 'Contato não retornado pela API' }}</small><small v-if="participant.email && participant.phone" class="mt-1 block truncate text-[8px] text-[#667394]">{{ participant.email }}</small></span>
              </div>
              <div v-else class="mt-4 flex items-center gap-3 text-[#9aa2b7]"><span class="grid size-12 place-items-center rounded-full bg-[#f4f1f7]"><span class="i-lucide-user-round size-5" /></span><span><strong class="block text-[10px]">Participante indisponível</strong><small class="mt-1 block text-[8px]">Não retornado pela API</small></span></div>
            </section>
          </div>
          <div class="flex min-h-[52px] items-center gap-2 border-t border-[#eeeaf0] px-4"><span class="text-[8px] font-semibold text-[#687394]">Participantes adicionais</span><div v-if="additionalParticipants.length" class="flex -space-x-2"><span v-for="participant in additionalParticipants" :key="participant.id" class="grid size-7 place-items-center overflow-hidden rounded-full border-2 border-white bg-[#efe9ff] text-[7px] font-bold text-[#5d24cb]"><img v-if="participant.avatar" :src="participant.avatar" :alt="participant.name" class="h-full w-full object-cover" /><template v-else>{{ participant.initials }}</template></span></div><small v-else class="text-[8px] text-[#98a1b4]">Não retornados pela API</small></div>
        </article>

        <article class="grid h-[81px] grid-cols-4 divide-x divide-[#eeeaf0] rounded-2xl border border-[#eeeaf0] bg-white shadow-[0_3px_15px_rgba(51,31,93,0.03)]">
          <div class="flex items-center gap-3 px-4"><span class="grid size-8 place-items-center rounded-full bg-[#f1ecff] text-[#5721c8]"><span class="i-lucide-calendar-days size-4" /></span><span><small class="block text-[8px] text-[#667394]">Data</small><strong class="mt-1 block text-[9px]">{{ formatDate(scheduledAt) }}</strong></span></div>
          <div class="flex items-center gap-3 px-4"><span class="grid size-8 place-items-center rounded-full bg-[#f1ecff] text-[#5721c8]"><span class="i-lucide-clock-3 size-4" /></span><span><small class="block text-[8px] text-[#667394]">Horário</small><strong class="mt-1 block text-[9px]">{{ formatTime(scheduledAt) }} <template v-if="endsAt">— {{ formatTime(endsAt) }}</template></strong></span></div>
          <div class="flex items-center gap-3 px-4"><span class="grid size-8 place-items-center rounded-full bg-[#f1ecff] text-[#5721c8]"><span class="i-lucide-users-round size-4" /></span><span><small class="block text-[8px] text-[#667394]">Tipo de visita</small><strong class="mt-1 block text-[9px]">{{ textValue(visitType).replaceAll('_', ' ') }}</strong></span></div>
          <div class="flex items-center gap-3 px-4"><span class="grid size-8 place-items-center rounded-full bg-[#f1ecff] text-[#5721c8]"><span class="i-lucide-calendar-plus size-4" /></span><span><small class="block text-[8px] text-[#667394]">Agendada em</small><strong class="mt-1 block text-[9px]">{{ formatDateTime(createdAt) }}</strong><small v-if="scheduledBy" class="mt-0.5 block text-[8px] text-[#667394]">por {{ scheduledBy }}</small></span></div>
        </article>

        <article class="h-[105px] rounded-2xl border border-[#eeeaf0] bg-white px-5 py-3 shadow-[0_3px_15px_rgba(51,31,93,0.03)]">
          <div class="relative flex h-full items-start justify-between">
            <span class="absolute left-10 right-10 top-[18px] h-px bg-[#e6e1eb]" />
            <span v-for="stage in timelineStages" :key="stage.label" class="relative z-10 flex w-1/4 flex-col items-center text-center"><span class="grid size-9 place-items-center rounded-full border bg-white" :class="stage.complete ? 'border-[#7f48e5] text-[#5721c8]' : 'border-[#dcd7e5] text-[#9ca5b6]'"><span :class="stage.icon" class="size-4" /></span><strong class="mt-2 text-[9px]" :class="stage.complete ? 'text-[#5721c8]' : 'text-[#667394]'">{{ stage.label }}</strong><small class="mt-1 text-[8px] text-[#7b86a1]">{{ stage.date }}</small></span>
          </div>
        </article>

        <article class="min-h-[80px] rounded-2xl border border-[#e3f2ec] bg-gradient-to-r from-[#f6fcfa] to-[#eef9f8] px-5 py-4 shadow-[0_3px_15px_rgba(51,31,93,0.025)]">
          <div class="flex gap-3"><span class="grid size-7 shrink-0 place-items-center rounded-lg bg-emerald-100 text-emerald-700"><span class="i-lucide-lock-keyhole size-4" /></span><span class="min-w-0"><strong class="block text-[9px] text-[#3f5e64]">Instruções de acesso</strong><p v-if="accessInstructions" class="mt-2 text-[8px] leading-4 text-[#557179]">{{ accessInstructions }}</p><p v-else-if="accessDetails.length" class="mt-2 text-[8px] leading-4 text-[#557179]"><span v-for="detail in accessDetails" :key="detail.label" class="mr-3"><b>{{ detail.label }}:</b> {{ detail.value }}</span></p><p v-else class="mt-2 text-[8px] text-[#7f969d]">Informações de acesso não retornadas pela API.</p></span></div>
        </article>

        <div class="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-[9px]">
          <article class="min-h-[144px] rounded-2xl border border-[#eeeaf0] bg-white p-4 shadow-[0_3px_15px_rgba(51,31,93,0.03)]"><header class="flex items-center justify-between"><h2 class="flex items-center gap-2 text-[10px] font-bold"><span class="i-lucide-clipboard-check size-4 text-[#5821ca]" /> Checklist da visita</h2><span v-if="checklistItems.length" class="rounded-full bg-emerald-50 px-3 py-1 text-[8px] font-semibold text-emerald-700">{{ checklistItems.filter(item => item.complete).length }} / {{ checklistItems.length }}</span><span v-else class="rounded-full bg-[#f4f1f7] px-3 py-1 text-[8px] text-[#8992a8]">—</span></header><div v-if="checklistItems.length" class="mt-4 space-y-3"><div v-for="item in checklistItems" :key="item.id" class="flex items-center gap-2"><span class="grid size-4 place-items-center rounded-full" :class="item.complete ? 'bg-emerald-100 text-emerald-700' : 'bg-[#f2eef6] text-[#9ca5b6]'"><span :class="item.complete ? 'i-lucide-check' : 'i-lucide-minus'" class="size-3" /></span><span class="min-w-0 flex-1"><strong class="block truncate text-[8px]">{{ item.title }}</strong><small v-if="item.note" class="mt-0.5 block truncate text-[7px] text-[#78839e]">{{ item.note }}</small></span></div></div><p v-else class="mt-5 rounded-lg bg-[#faf9fc] px-3 py-3 text-[8px] leading-4 text-[#8790a7]">Checklist não retornado pela API para esta visita.</p></article>
          <article class="min-h-[144px] rounded-2xl border border-[#eeeaf0] bg-white p-4 shadow-[0_3px_15px_rgba(51,31,93,0.03)]"><header class="flex items-center justify-between"><h2 class="flex items-center gap-2 text-[10px] font-bold"><span class="i-lucide-paperclip size-4 text-[#5821ca]" /> Arquivos</h2><span class="text-[8px] text-[#8992a8]">{{ attachments.length || '—' }}</span></header><div v-if="attachments.length" class="mt-4 space-y-2"><div v-for="file in attachments" :key="file.id" class="flex items-center gap-3 rounded-lg border border-[#f0edf4] px-3 py-2"><span class="grid size-7 place-items-center rounded-md bg-rose-50 text-rose-600"><span class="i-lucide-file-text size-3.5" /></span><span class="min-w-0 flex-1"><strong class="block truncate text-[8px]">{{ file.name }}</strong><small class="mt-0.5 block truncate text-[7px] text-[#78839e]">{{ file.type || 'Tipo não retornado' }}<template v-if="file.size"> · {{ file.size }}</template></small></span></div></div><p v-else class="mt-5 rounded-lg bg-[#faf9fc] px-3 py-3 text-[8px] leading-4 text-[#8790a7]">Arquivos não retornados pela API para esta visita.</p></article>
        </div>
      </section>

      <aside class="space-y-[9px]">
        <article class="h-[195px] overflow-hidden rounded-2xl border border-[#eeeaf0] bg-white p-4 shadow-[0_3px_15px_rgba(51,31,93,0.03)]"><header class="flex items-center justify-between"><h2 class="flex items-center gap-2 text-[9px] font-bold text-[#4f1cc7]"><span class="i-lucide-sparkles size-3.5" /> Como ganhar pontos nesta visita</h2><span class="text-[8px] text-[#8b94aa]">—</span></header><div class="mt-5 flex items-center gap-3 rounded-xl bg-[#faf9fc] px-3 py-4"><span class="grid size-9 place-items-center rounded-full bg-[#f1eef5] text-[#9ca5b6]"><span class="i-lucide-award size-4" /></span><p class="text-[8px] leading-4 text-[#727e99]">A API de visitas não retornou regras de pontuação para este registro.</p></div></article>
        <article class="h-[336px] overflow-hidden rounded-2xl border border-[#eeeaf0] bg-white p-4 shadow-[0_3px_15px_rgba(51,31,93,0.03)]"><header class="flex items-center justify-between"><h2 class="text-[11px] font-medium">Feed de atividade</h2><span class="text-[8px] text-[#5922c8]">{{ activityFeed.length ? activityFeed.length : '—' }}</span></header><div v-if="activityFeed.length" class="mt-3 divide-y divide-[#f0edf4]"><article v-for="activity in activityFeed" :key="activity.id" class="flex gap-2 py-3"><img v-if="activity.avatar" :src="activity.avatar" :alt="activity.actor" class="size-7 shrink-0 rounded-full object-cover" /><span v-else class="grid size-7 shrink-0 place-items-center rounded-full bg-violet-100 text-[8px] font-bold text-[#5a24c7]">{{ initials(activity.actor) }}</span><span class="min-w-0 flex-1"><strong class="block truncate text-[8px]">{{ activity.actor }}</strong><small class="mt-1 block line-clamp-2 text-[8px] leading-4 text-[#68749a]">{{ activity.label }}</small><small class="mt-1 block text-[7px] text-[#8b94aa]">{{ activity.date }}</small></span></article></div><div v-else class="mt-4 flex h-[246px] flex-col items-center justify-center rounded-xl bg-[#faf9fc] px-5 text-center"><span class="grid size-9 place-items-center rounded-full bg-[#f1eef5] text-[#9ca5b6]"><span class="i-lucide-message-square-off size-4" /></span><strong class="mt-3 text-[9px] text-[#6f7892]">Atividade indisponível</strong><small class="mt-2 text-[8px] leading-4 text-[#8a93a9]">O histórico não foi retornado pela API desta visita.</small></div><div class="mt-3 flex h-8 items-center rounded-lg border border-[#eeeaf2] px-3 text-[8px] text-[#9aa2b5]"><span class="i-lucide-message-square size-3" /><span class="ml-2">Comentários indisponíveis pela API</span></div></article>
        <article class="!mt-[14px] h-[89px] overflow-hidden rounded-2xl border border-[#eeeaf0] bg-white p-3 shadow-[0_3px_15px_rgba(51,31,93,0.03)]"><header class="flex items-center justify-between"><h2 class="text-[10px] font-medium">Missão do dia</h2><span class="rounded-full bg-[#f1eef5] px-2 py-1 text-[8px] text-[#8c95ab]">—</span></header><div class="mt-2 flex items-center gap-3"><span class="grid size-8 place-items-center rounded-lg bg-[#f4f1f7] text-[#9ca5b6]"><span class="i-lucide-party-popper size-4" /></span><small class="leading-4 text-[#78839d]">Metas não retornadas pela API de visitas.</small></div></article>
        <article class="h-[181px] overflow-hidden rounded-2xl border border-[#eeeaf0] bg-white p-4 shadow-[0_3px_15px_rgba(51,31,93,0.03)]"><header class="flex items-center justify-between"><h2 class="text-[10px] font-medium">Ranking da semana</h2><span class="text-[8px] text-[#5922c8]">—</span></header><div class="mt-4 flex items-center gap-3 rounded-xl bg-[#faf9fc] px-3 py-3"><span class="grid size-8 place-items-center rounded-full bg-[#f1eef5] text-[#9ca5b6]"><span class="i-lucide-trophy size-4" /></span><small class="leading-4 text-[#78839d]">Ranking não retornado pela API de visitas.</small></div></article>
      </aside>
    </section>

    <p v-if="!detailAvailable && !actionError" class="mt-4 w-[820px] rounded-xl border border-violet-100 bg-violet-50 px-4 py-3 text-[10px] text-[#6540a8]">Os detalhes da visita ainda não foram retornados pela API.</p>
  </main>
</template>
